import React, { createContext, useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import {
    getFirestore,
    collection,
    doc,
    onSnapshot,
    setDoc,
    updateDoc,
    query,
    where,
    getDocs,
    getDoc
} from 'firebase/firestore';
import { useDataStore } from './datastore-provider';
import { useFirebase } from '../firebase/firebase-provider';

interface UserSettings {
    username?: string;
    [key: string]: any;
}

interface SettingsContextType {
    publicSettings: UserSettings | null;
    privateSettings: UserSettings | null;
    getUserByUsername: (username: string) => Promise<any>;
    getUsersByUsername: (usernames: string[]) => Promise<any>;
    createUsername: (username: string) => Promise<void>;
    updateSettings: (newSettings: any) => void;
    getUsersById: (ids: string[]) => Promise<any>;
    setHomeId: (id: string | null) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
    const context = React.useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

const COLLECTION_NAME = 'settings';

SettingsProvider.propTypes = {
    children: PropTypes.element.isRequired,
};
export function SettingsProvider(props: any) {
    const [privateSettings, setPrivateSettings] = useState<UserSettings | null>(null);
    const [publicSettings, setPublicSettings] = useState<UserSettings | null>(null);
    const { userDb } = useDataStore();
    const app = useFirebase();

    // Private
    useEffect(() => {
        if (userDb) {
            const db = getFirestore(app);
            const privateDocRef = doc(collection(userDb, COLLECTION_NAME), 'private');
            const unsubscribe = onSnapshot(privateDocRef, (doc: any) => {
                if (!doc.exists()) {
                    setPrivateSettings({} as UserSettings);
                } else {
                    setPrivateSettings(doc.data() as UserSettings);
                }
            }, (error: any) => {
                console.debug(error);
                setPrivateSettings(privateSettings || ({} as UserSettings));
                // TODO: Let the user know something went wrong
            });
            return unsubscribe;
        } else if (userDb === null) {
            setPrivateSettings({} as UserSettings);
        } else {
            setPrivateSettings(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDb]);

    // Public
    useEffect(() => {
        if (userDb) {
            const unsubscribe = onSnapshot(userDb, (doc: any) => {
                if (doc.exists() || doc.data()) {
                    setPublicSettings(doc.data() as UserSettings);
                } else {
                    setPublicSettings({} as UserSettings);
                }
            }, (error: any) => {
                console.debug(error);
                setPublicSettings(publicSettings || ({} as UserSettings));
                // TODO: Let the user know something went wrong
            });
            return unsubscribe;
        } else if (userDb === null) {
            setPublicSettings({} as UserSettings);
        } else {
            setPublicSettings(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDb]);

    function getUserByUsername(username: any) {
        return new Promise((resolve, reject) => {
            if (!username) resolve(null);
            const db = getFirestore(app);
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('username', '==', username.toLocaleLowerCase()));
            getDocs(q)
                .then((querySnapshot: any) => {
                    if (!querySnapshot.docs || !querySnapshot.docs.length) {
                        resolve(null);
                    } else {
                        const doc = querySnapshot.docs[0];
                        resolve({ ...doc.data(), id: doc.id });
                    }
                })
                .catch((err: any) => {
                    console.debug(err);
                    reject(err);
                });
        });
    }

    function getUsersById(ids: any) {
        return new Promise((resolve) => {
            if (!ids || !ids.length) resolve([]);
            Promise.all(ids.map(getUserById)).then((users) => {
                resolve(users.filter(Boolean)); // Filter out nulls in case there were errors
            });
        });
    }

    function getUserById(id: any) {
        return new Promise((resolve) => {
            if (!id) resolve(null);
            const db = getFirestore(app);
            const userDocRef = doc(db, 'users', id);
            getDoc(userDocRef)
                .then((doc: any) => {
                    if (!doc.exists()) {
                        resolve(null);
                    } else {
                        resolve({ ...doc.data(), id: doc.id });
                    }
                })
                .catch((err: any) => {
                    console.debug(err);
                });
        });
    }

    function getUsersByUsername(usernames: any) {
        return new Promise((resolve, reject) => {
            if (!usernames || !usernames.length) {
                resolve([]);
            } else {
                const lowerUsernames = usernames.map((u: any) => u.toLocaleLowerCase());
                const db = getFirestore(app);
                const usersRef = collection(db, 'users');
                const q = query(usersRef, where('username', 'in', lowerUsernames));
                getDocs(q)
                    .then((querySnapshot: any) => {
                        if (!querySnapshot.docs || !querySnapshot.docs.length) {
                            resolve([]);
                        } else {
                            resolve(querySnapshot.docs.map((doc: any) => ({
                                ...doc.data(),
                                id: doc.id
                            })));
                            // const doc = querySnapshot.docs[0];
                            // resolve({ ...doc.data(), id: doc.id });
                        }
                    })
                    .catch((err: any) => {
                        console.debug(err);
                        reject(err);
                    });
            }
        });
    }

    function createUsername(username: any) {
        return new Promise<void>((resolve, reject) =>
            setDoc(userDb, { username: username.toLocaleLowerCase() }, { merge: true })
            .then(() => {
                resolve();
            })
            .catch((err: any) => {
                console.debug(err);
                reject(err);
            }));
    }

    function updatePublicSettings(newSettings: any) {
        if (userDb) {
            updateDoc(userDb, newSettings).then(() => {});
        }
    }

    function updatePrivateSettings(change: any) {
        if (userDb) {
            const privateDocRef = doc(collection(userDb, COLLECTION_NAME), 'private');
            setDoc(privateDocRef, change, { merge: true }).then(() => {});
        }
    }

    function setHomeId(id: any) {
        updatePrivateSettings({ homeId: id });
    }

    return (
        <SettingsContext.Provider value={{ publicSettings, privateSettings, getUserByUsername, getUsersByUsername, createUsername, updateSettings: updatePublicSettings, getUsersById, setHomeId }}>
            { props.children }
        </SettingsContext.Provider>
    );
}
